import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ArrowsAiResourcesAPI from 'dashboard/api/arrowsAi/resources';
import { fromApi } from 'dashboard/composables/arrowsAi/resourceMapper';
import {
  parseAPIErrorResponse,
  throwErrorMessage,
} from 'dashboard/store/utils/api';

export const MAX_FILES_PER_BATCH = 10;

export function useArrowsAiResources(assistantId) {
  const { t } = useI18n();
  const resources = ref([]);
  const isLoading = ref(false);
  let loadedAssistantId = null;

  const fetchResources = async (id, { force } = {}) => {
    if (!id) {
      resources.value = [];
      loadedAssistantId = null;
      return;
    }
    if (!force && loadedAssistantId === id) return;

    isLoading.value = true;
    try {
      const { data } = await ArrowsAiResourcesAPI.get({ assistantId: id });
      resources.value = (Array.isArray(data) ? data : []).map(fromApi);
      loadedAssistantId = id;
    } catch (error) {
      resources.value = [];
      loadedAssistantId = id;
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string'
          ? message
          : t('ARROWS_AI.RESOURCES.FETCH_ERROR')
      );
    } finally {
      isLoading.value = false;
    }
  };

  watch(assistantId, id => fetchResources(id), { immediate: true });

  const addResources = async files => {
    try {
      const { data } = await ArrowsAiResourcesAPI.create({
        assistantId: assistantId.value,
        files,
      });
      const created = (Array.isArray(data) ? data : []).map(fromApi);
      resources.value = [...created, ...resources.value];
      return created;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const updateResource = async (id, payload) => {
    try {
      const { data } = await ArrowsAiResourcesAPI.update({
        assistantId: assistantId.value,
        id,
        payload,
      });
      const updated = fromApi(data);
      resources.value = resources.value.map(item =>
        item.id === updated.id ? updated : item
      );
      return updated;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteResources = async ids => {
    const uniqueIds = [...new Set(ids.map(Number))];
    try {
      await Promise.all(
        uniqueIds.map(id =>
          ArrowsAiResourcesAPI.delete({ assistantId: assistantId.value, id })
        )
      );
      const idSet = new Set(uniqueIds);
      resources.value = resources.value.filter(item => !idSet.has(item.id));
    } catch (error) {
      await fetchResources(assistantId.value, { force: true });
      throwErrorMessage(error);
    }
  };

  return {
    resources: computed(() => resources.value),
    isLoading: computed(() => isLoading.value),
    addResources,
    updateResource,
    deleteResources,
  };
}
